/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/roles-permissions/users-table/index.tsx
 * (non-ecommerce, same source `customers/List.tsx` cites), simplified: `GET /api/identity/invitations`
 * takes no paging or filter parameters (`ListInvitationsQuery.HandleAsync`) and answers the whole
 * outstanding set, so it is loaded once and paged in memory — same shape as
 * `pages/integrations/tally/Queue.tsx`'s `result={{ items, page: 1, pageSize: items.length, totalCount: items.length }}`.
 *
 * Only the data, columns and labels are ours.
 */
import { Link, useLoaderData, useOutletContext } from "react-router-dom";
import { PiPlusBold as Plus } from "react-icons/pi";
import { Button } from "rizzui";
import { EmptyState, ErrorSurface } from "@steelyard/theme";
import { PagedTable } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { TableSearch } from "../../components/table/toolbar";
import { invitationColumns } from "./columns";
import { listInvitations } from "../../lib/services/invitations";
import { can } from "../../lib/permissions";
import type { AccountSummary, Permission, PermissionState } from "../../types";

// `company.user.invite` is the code `InvitationController.cs`'s own `[Authorize]` attributes
// require on every route this screen calls. It is in `types/index.ts`'s `Permission` union and
// `lib/permissions.ts`'s `ALL_PERMISSIONS`, so `can()` reads the live grant.
const INVITE_PERMISSION: Permission = "company.user.invite";

export async function invitationsListLoader() {
  return listInvitations();
}

export default function InvitationsList() {
  const { items, totalCount } = useLoaderData() as Awaited<ReturnType<typeof invitationsListLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const canInvite = can(permissionState, INVITE_PERMISSION);

  if (!canInvite) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="manage invitations" />;
  }

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Invitations"
        count={totalCount}
        description="Outstanding invitations — sent, not yet accepted, revoked or expired away."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Invitations" }]}
      />

      <PagedTable
        actions={
          <Link to="/invitations/new">
            <Button as="span">
              <Plus aria-hidden className="me-1.5 size-4" />
              New invitation
            </Button>
          </Link>
        }
        result={{ items, page: 1, pageSize: items.length, totalCount: items.length }}
        caption="Outstanding invitations: email, role, sent and expiry dates"
        columns={invitationColumns}
        getRowId={(row) => row.id}
        // The route takes no search parameter: this filters the whole outstanding set already loaded.
        search={(table) => <TableSearch table={table} placeholder="Search invitations…" />}
        empty={
          <EmptyState
            title="No outstanding invitations"
            action={
              <Link to="/invitations/new">
                <Button as="span" size="sm">
                  <Plus aria-hidden />
                  New invitation
                </Button>
              </Link>
            }
          >
            Every invitation sent has been accepted, revoked, or none has been sent yet.
          </EmptyState>
        }
      />
    </div>
  );
}
